import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  fetchWithTimeout,
  formatHash,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

const responseFieldNames = [
  'user_agent',
  'tls_version',
  'tls_cipher',
  'ja4',
  'ja4h',
] as const;

function responseFields(response: RawData): RawData {
  return Object.fromEntries(
    responseFieldNames.flatMap((name) =>
      name in response ? [[name, response[name]]] : [],
    ),
  );
}

async function collect(): Promise<RawData> {
  const fetchResponse = await fetchWithTimeout(
    'https://ja4checker.com/api/whoami',
    { cache: 'no-store' },
    5000,
    'TLS endpoint request timed out after 5 seconds',
  );
  if (!fetchResponse.ok) {
    throw new Error(`TLS endpoint returned ${fetchResponse.status}`);
  }
  const response: unknown = await fetchResponse.json();
  if (!isRecord(response)) {
    throw new Error('TLS endpoint returned invalid JSON');
  }
  return responseFields(response);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const reasons: string[] = [];
  if (!/^TLSv?1\.3$/i.test(String(rawData.tls_version))) {
    reasons.push('TLS 1.3 was not negotiated');
  }
  if (!String(rawData.tls_cipher)) {
    reasons.push('Negotiated TLS cipher is missing');
  }
  if (!/^[a-z0-9]{10}_[a-f0-9]{12}_[a-f0-9]{12}$/.test(String(rawData.ja4))) {
    reasons.push('JA4 fingerprint has an invalid format');
  }
  if (!/^[a-z0-9]+(?:_[a-f0-9]{12})+$/.test(String(rawData.ja4h))) {
    reasons.push('JA4H fingerprint has an invalid format');
  }
  const browserUserAgent = context.results.browserIdentity?.rawData.userAgent;
  if (
    typeof browserUserAgent == 'string' &&
    rawData.user_agent != browserUserAgent
  ) {
    reasons.push('TLS endpoint User-Agent does not match Browser identity');
  }
  return validation(reasons);
}

export const ja4FingerprintTest: TestDefinition = {
  id: 'ja4Fingerprint',
  title: 'JA4 / JA4H fingerprint',
  category: 'shared',
  format,
  run,
  validate,
};
